// components/tour/TourComplete.jsx
import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Avatar,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  AccountCircle as AccountCircleIcon,
  Explore as ExploreIcon
} from '@mui/icons-material';
import { useTour } from './TourContext';
import { getDecodedToken } from '../../utils/auth';

const TourComplete = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { completeTour } = useTour();

  // Get username from token
  const decodedToken = getDecodedToken();
  const username = decodedToken?.user?.username || 'there';

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10002,
        p: 2
      }}
    >
      <Paper
        elevation={16}
        sx={{
          maxWidth: isMobile ? 350 : 450,
          width: '100%',
          p: isMobile ? 3 : 4,
          borderRadius: 3,
          textAlign: 'center',
          backgroundColor: 'background.paper',
          border: `2px solid ${theme.palette.success.main}`,
          boxShadow: `
            0 16px 64px rgba(0, 0, 0, 0.4),
            0 0 0 1px rgba(76, 175, 80, 0.2)
          `,
          animation: 'slideIn 0.4s ease-out',
          '@keyframes slideIn': {
            from: {
              opacity: 0,
              transform: 'translateY(-20px) scale(0.95)'
            },
            to: {
              opacity: 1,
              transform: 'translateY(0) scale(1)'
            }
          }
        }}
      >
        {/* Success Icon */}
        <Avatar
          sx={{
            width: isMobile ? 64 : 80,
            height: isMobile ? 64 : 80,
            bgcolor: theme.palette.success.main,
            mx: 'auto',
            mb: 3,
            boxShadow: theme.shadows[8]
          }}
        >
          <CheckCircleIcon sx={{ fontSize: isMobile ? '2rem' : '2.5rem' }} />
        </Avatar>

        {/* Completion Text */}
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: 'text.primary',
            mb: 1,
            fontSize: isMobile ? '1.5rem' : '2rem'
          }}
        >
          Great job, {username}! 🎉
        </Typography>

        <Typography
          variant="h6"
          sx={{
            fontWeight: 500,
            color: 'success.main',
            mb: 2,
            fontSize: isMobile ? '1rem' : '1.25rem'
          }}
        >
          You've completed the tour!
        </Typography>

        <Typography
          variant="body1"
          sx={{
            color: 'text.secondary',
            mb: 3,
            lineHeight: 1.6,
            fontSize: isMobile ? '0.875rem' : '1rem'
          }}
        >
          You're now ready to start your language learning journey. Remember, you can access this tour anytime by clicking on your avatar in the top-right corner.
        </Typography>

        {/* Access Instructions */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2,
            bgcolor: 'grey.50',
            borderRadius: 2,
            mb: 4,
            border: `1px dashed ${theme.palette.grey[300]}`
          }}
        >
          <Avatar
            sx={{
              width: 32,
              height: 32,
              bgcolor: theme.palette.primary.main,
              mr: 2
            }}
            data-tour="user-avatar"
          >
            <AccountCircleIcon fontSize="small" />
          </Avatar>
          <Box sx={{ textAlign: 'left' }}>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}
            >
              Future Access
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', fontSize: '0.75rem' }}
            >
              Click your avatar → "How to use"
            </Typography>
          </Box>
        </Box>

        {/* Action Button */}
        <Button
          variant="contained"
          onClick={completeTour}
          startIcon={<ExploreIcon />}
          size={isMobile ? 'medium' : 'large'}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            py: isMobile ? 1.5 : 2,
            px: 4,
            fontSize: isMobile ? '0.875rem' : '1rem',
            boxShadow: theme.shadows[4],
            backgroundColor: theme.palette.success.main,
            '&:hover': {
              backgroundColor: theme.palette.success.dark,
              boxShadow: theme.shadows[8]
            }
          }}
        >
          Start Learning!
        </Button>

        {/* Encouragement */}
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            color: 'text.secondary',
            mt: 3,
            opacity: 0.7,
            fontSize: '0.75rem',
            fontStyle: 'italic'
          }}
        >
          "The journey of a thousand miles begins with a single step." - Lao Tzu
        </Typography>
      </Paper>
    </Box>
  );
};

export default TourComplete;